import { c as _c } from "react/compiler-runtime";
import { Menu, Search, type MenuItem } from 'platform/components';
import { CategoryColorScheme } from '../../../../../../constants/categoryColors';
import { buildCategoryItems, type CategoryItem } from '../../../../../../hooks/useFilterCategorySelection';
import type { EagColumnOrGroup } from '../../../../../../types/EagColumnGroup';
import type { FilterState } from '../../../../../../types/FilterState';
type FilterCategoryMenuProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  selectedCategoryId: string | null;
  onCategorySelect: (categoryId: string) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
};

// Re-export so existing consumers of FilterCategoryMenu's exports keep working.
export type { CategoryItem };
export { buildCategoryItems };
const toBadgeColorScheme = (color: CategoryColorScheme) => color as 'blue' | 'orange' | 'green' | 'purple' | 'teal' | 'red' | 'magenta' | 'yellow' | 'neutral';

/**
 * Left sidebar category list for the desktop 75vw FilterPanel.
 *
 * Uses the platform `Menu` component for rendering (consistent design system look),
 * with a custom search input placed above it.  The `Menu` is NOT rendered with
 * `isSearchable` because platform `Menu` only filters by item label — it cannot
 * search inside a category's filter children.  Instead this component owns the
 * search state and pre-filters the items list before passing it to `Menu`.
 *
 * ## Cross-category search
 *
 * When `searchTerm` is non-empty, a category is shown if:
 * 1. Its own label contains the search term (category name match), OR
 * 2. Any of its filterable children's `headerName` values contain the search term.
 *
 * Searching "External Id" will surface "General" (or whatever category contains
 * the External Id filter) even though the category label itself doesn't match.
 *
 * The same `searchTerm` is forwarded to `FilterContentArea` so filter widgets
 * within the selected category are also narrowed down.
 *
 * ## Why no store reads here
 *
 * `filterState`, `selectedCategoryId`, and `searchTerm` are all props from
 * `FilterPanel`, keeping this component pure/presentational and store-agnostic.
 */
export function FilterCategoryMenu(props) {
  const $ = _c(16);
  let t0;
  if ($[0] !== props.columnConfig || $[1] !== props.filterState || $[2] !== props.searchTerm) {
    const allItems = buildCategoryItems(props.columnConfig, props.filterState);
    const lowerSearch = props.searchTerm.toLowerCase();
    const visibleItems = lowerSearch ? allItems.filter(item => item.label.toLowerCase().includes(lowerSearch) || item.childLabels.some(label => label.includes(lowerSearch))) : allItems;
    t0 = visibleItems.map(_temp);
    $[0] = props.columnConfig;
    $[1] = props.filterState;
    $[2] = props.searchTerm;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const menuItems = t0;
  const t1 = props.searchTerm || null;
  let t2;
  if ($[4] !== props) {
    t2 = value => props.onSearchChange(value ?? "");
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="px-3 pt-3 pb-2 flex-shrink-0"><Search value={t1} onChange={t2} data-testid="filter-category-search" /></div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = props.selectedCategoryId ?? undefined;
  let t5;
  if ($[9] !== menuItems || $[10] !== props.onCategorySelect || $[11] !== t4) {
    t5 = <div className="flex-1 overflow-y-auto px-2 py-1"><Menu items={menuItems} activeItemId={t4} onItemChange={props.onCategorySelect} data-testid="filter-category-menu" /></div>;
    $[9] = menuItems;
    $[10] = props.onCategorySelect;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <div className="flex flex-col h-full overflow-hidden">{t3}{t5}</div>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(item_0) {
  return {
    id: item_0.id,
    label: item_0.label,
    badge: item_0.activeCount > 0 ? {
      value: item_0.activeCount,
      colorScheme: toBadgeColorScheme(item_0.color)
    } : undefined
  };
}